import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { SchoolSelect, useSchools } from "../../components/SchoolSelect";
import { Coins, Users, Clock, ChevronDown, ChevronUp, TrendingUp, TrendingDown, Download } from "lucide-react";
import { localName, localFullName } from "../../lib/localName";

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function fmtMoney(v) {
  return (v || 0).toLocaleString("ar-SA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtHours(h) {
  return (h || 0).toLocaleString("ar-SA", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function StatusPill({ status }) {
  const map = {
    present: { cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400", label: "حاضر" },
    late: { cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400", label: "متأخر" },
    absent: { cls: "bg-rose-500/15 text-rose-700 dark:text-rose-400", label: "غائب" },
    excused: { cls: "bg-sky-500/15 text-sky-700 dark:text-sky-400", label: "بعذر" },
  };
  const s = map[status] || map.present;
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${s.cls}`}>{s.label}</span>;
}

function EmployeeRow({ row, expanded, onToggle, t, lang }) {
  return (
    <>
      <tr
        className={`border-t border-border hover:bg-primary/5 transition cursor-pointer ${expanded ? "bg-primary/5" : ""}`}
        onClick={onToggle}
        data-testid={`payroll-row-${row.employee_id}`}
      >
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            <div>
              <div className="font-semibold">{localFullName(row, lang)}</div>
              <div className="text-xs text-muted-foreground">{row.position || "—"} · {row.department || "—"}</div>
            </div>
          </div>
        </td>
        <td className="px-3 py-3 text-center tabular-nums">
          <span className="text-emerald-600 font-semibold">{row.days_present}</span>
          <span className="text-muted-foreground"> / {row.workdays_in_month}</span>
        </td>
        <td className="px-3 py-3 text-center tabular-nums text-rose-600 font-semibold">{row.days_absent}</td>
        <td className="px-3 py-3 text-center tabular-nums">{fmtHours(row.total_hours)}</td>
        <td className="px-3 py-3 text-center tabular-nums text-emerald-600">{fmtHours(row.overtime_hours)}</td>
        <td className="px-3 py-3 text-center tabular-nums text-amber-600">{fmtHours(row.short_hours)}</td>
        <td className="px-3 py-3 text-end tabular-nums font-mono">{fmtMoney(row.base_salary)}</td>
        <td className="px-3 py-3 text-end tabular-nums font-mono text-rose-600">-{fmtMoney(row.deduction)}</td>
        <td className="px-4 py-3 text-end tabular-nums font-mono font-bold text-primary">{fmtMoney(row.net_salary)}</td>
      </tr>
      {expanded && (
        <tr className="bg-muted/30 border-t border-border">
          <td colSpan={9} className="px-4 py-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
              {t("hr_daily_detail") || "تفصيل يومي"} — {row.daily.length} {t("hr_days") || "يوم"}
            </div>
            {row.daily.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4 text-center">
                {t("hr_no_records_month") || "لا توجد سجلات دوام لهذا الشهر"}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-muted-foreground border-b border-border">
                      <th className="text-start py-2 px-2">{t("date") || "التاريخ"}</th>
                      <th className="text-center py-2 px-2">{t("hr_check_in") || "دخول"}</th>
                      <th className="text-center py-2 px-2">{t("hr_check_out") || "خروج"}</th>
                      <th className="text-center py-2 px-2">{t("hr_worked_hours") || "ساعات العمل"}</th>
                      <th className="text-center py-2 px-2">{t("hr_late") || "تأخير"}</th>
                      <th className="text-end py-2 px-2">{t("status") || "الحالة"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {row.daily.map((d) => (
                      <tr key={d.date} className="border-b border-border/50">
                        <td className="py-2 px-2 font-mono">{d.date}</td>
                        <td className="py-2 px-2 text-center font-mono tabular-nums">{d.check_in || "—"}</td>
                        <td className="py-2 px-2 text-center font-mono tabular-nums">{d.check_out || "—"}</td>
                        <td className="py-2 px-2 text-center tabular-nums font-semibold">{fmtHours(d.worked_hours)}</td>
                        <td className="py-2 px-2 text-center tabular-nums">{d.late_min ? `+${d.late_min}د` : "—"}</td>
                        <td className="py-2 px-2 text-end"><StatusPill status={d.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function EmployeeCardMobile({ row, t, lang }) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="p-4 border rounded-2xl shadow-sm" data-testid={`payroll-card-${row.employee_id}`}>
      <div className="flex justify-between items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-semibold truncate">{localFullName(row, lang)}</div>
          <div className="text-xs text-muted-foreground truncate">{row.position || "—"}</div>
        </div>
        <div className="text-end">
          <div className="text-lg font-bold text-primary tabular-nums font-mono">{fmtMoney(row.net_salary)}</div>
          <div className="text-[10px] text-muted-foreground">صافي · ريال</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3 text-center">
        <div className="bg-muted/40 rounded-lg py-2">
          <div className="text-sm font-semibold text-emerald-600 tabular-nums">{row.days_present}/{row.workdays_in_month}</div>
          <div className="text-[10px] text-muted-foreground">حضور</div>
        </div>
        <div className="bg-muted/40 rounded-lg py-2">
          <div className="text-sm font-semibold tabular-nums">{fmtHours(row.total_hours)}س</div>
          <div className="text-[10px] text-muted-foreground">ساعات</div>
        </div>
        <div className="bg-muted/40 rounded-lg py-2">
          <div className="text-sm font-semibold text-rose-600 tabular-nums">-{fmtMoney(row.deduction)}</div>
          <div className="text-[10px] text-muted-foreground">خصم</div>
        </div>
      </div>
      <Button variant="ghost" size="sm" className="w-full mt-2" onClick={() => setOpen(v => !v)}>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        {open ? (t("hide") || "إخفاء") : (t("hr_daily_detail") || "تفصيل يومي")}
      </Button>
      {open && row.daily.length > 0 && (
        <div className="mt-2 border-t border-border pt-2 space-y-1 max-h-64 overflow-y-auto">
          {row.daily.map(d => (
            <div key={d.date} className="flex items-center justify-between text-xs py-1">
              <span className="font-mono text-muted-foreground">{d.date}</span>
              <span className="font-mono tabular-nums">{d.check_in || "—"} / {d.check_out || "—"}</span>
              <span className="tabular-nums font-semibold">{fmtHours(d.worked_hours)}س</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function PayrollReportPage() {
  const {  t , lang } = useI18n();
  const { schools, schoolId, setSchoolId, isSuper, queryId } = useSchools();
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ month });
      if (queryId) params.set("school_id", queryId);
      const r = await api.get(`/hr/payroll-report?${params.toString()}`);
      setData(r.data);
    } catch (err) { setData(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, queryId]);

  const filteredEmployees = useMemo(() => {
    if (!data?.employees) return [];
    if (!search) return data.employees;
    const q = search.toLowerCase();
    return data.employees.filter(e =>
      (e.full_name || "").toLowerCase().includes(q) ||
      (e.full_name_ar || "").includes(search) ||
      (e.position || "").toLowerCase().includes(q)
    );
  }, [data, search]);

  const exportCSV = () => {
    if (!data?.employees) return;
    const rows = [
      ["الاسم", "الوظيفة", "أيام الدوام", "أيام حضور", "أيام غياب", "إجمالي الساعات", "إضافي", "ناقص", "الراتب الأساسي", "الخصم", "الصافي"],
      ...data.employees.map(r => [
        localFullName(r, lang), r.position, r.workdays_in_month, r.days_present, r.days_absent,
        r.total_hours, r.overtime_hours, r.short_hours, r.base_salary, r.deduction, r.net_salary
      ])
    ];
    const csv = "\uFEFF" + rows.map(r => r.map(x => `"${String(x ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `payroll-${month}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Hero */}
      <Card className="overflow-hidden border-0 shadow-lg">
        <div className="relative bg-gradient-to-br from-primary via-primary to-primary/80 text-primary-foreground p-6 sm:p-8">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 80% 0%, rgba(255,255,255,.4), transparent 40%)" }} />
          <div className="relative flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur grid place-items-center shrink-0">
                <Coins className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">{t("hr_payroll_report") || "كشوف رواتب الموظفين"}</h1>
                <p className="text-sm text-primary-foreground/80 mt-1">
                  {t("hr_payroll_report_desc") || "تقرير الحضور والرواتب المحسوبة تلقائياً من سجلات الدوام"}
                </p>
              </div>
            </div>
            <Button
              onClick={exportCSV}
              variant="secondary"
              className="bg-white/20 hover:bg-white/30 backdrop-blur border border-white/30 text-primary-foreground"
              data-testid="payroll-export-btn"
              disabled={!data?.employees?.length}
            >
              <Download className="w-4 h-4 me-2" /> {t("export_csv") || "تصدير CSV"}
            </Button>
          </div>
        </div>
      </Card>

      {/* Filter bar */}
      <Card className="p-4 border shadow-none flex flex-wrap items-center gap-3">
        <Users className="w-4 h-4 text-primary shrink-0" />
        <SchoolSelect
          schools={schools}
          value={schoolId}
          onChange={setSchoolId}
          testId="payroll-school-select"
          disabled={!isSuper}
        />
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-muted-foreground">{t("month") || "الشهر"}</label>
          <Input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="w-44"
            data-testid="payroll-month-input"
          />
        </div>
        <div className="flex-1 min-w-[200px]">
          <Input
            placeholder={t("hr_search_employee") || "ابحث عن موظف..."}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="payroll-search"
          />
        </div>
      </Card>

      {/* Summary strip */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4 border shadow-none">
            <Users className="w-5 h-5 text-primary mb-2" />
            <div className="text-2xl font-bold tabular-nums">{data.totals?.employees || 0}</div>
            <div className="text-xs text-muted-foreground">{t("hr_employees_count") || "عدد الموظفين"}</div>
          </Card>
          <Card className="p-4 border shadow-none">
            <Clock className="w-5 h-5 text-secondary mb-2" />
            <div className="text-2xl font-bold tabular-nums">{data.workdays_in_month} يوم</div>
            <div className="text-xs text-muted-foreground">{t("hr_workdays_month") || "أيام دوام الشهر"} · 8س/يوم</div>
          </Card>
          <Card className="p-4 border shadow-none">
            <TrendingDown className="w-5 h-5 text-rose-500 mb-2" />
            <div className="text-2xl font-bold tabular-nums font-mono">{fmtMoney(data.totals?.total_deductions)}</div>
            <div className="text-xs text-muted-foreground">{t("hr_total_deductions") || "إجمالي الخصومات"} · ريال</div>
          </Card>
          <Card className="p-4 border shadow-none bg-primary/5 border-primary/20">
            <TrendingUp className="w-5 h-5 text-primary mb-2" />
            <div className="text-2xl font-bold tabular-nums font-mono text-primary">{fmtMoney(data.totals?.total_net)}</div>
            <div className="text-xs text-muted-foreground">{t("hr_total_net") || "إجمالي الرواتب الصافية"} · ريال</div>
          </Card>
        </div>
      )}

      {/* Table (desktop) */}
      <Card className="border border-border shadow-none overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1000px]">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-start px-4 py-3 text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t("employee") || "الموظف"}</th>
                <th className="text-center px-3 py-3 text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t("hr_days_present") || "حضور/دوام"}</th>
                <th className="text-center px-3 py-3 text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t("hr_days_absent") || "غياب"}</th>
                <th className="text-center px-3 py-3 text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t("hr_total_hours") || "إجمالي س."}</th>
                <th className="text-center px-3 py-3 text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t("hr_overtime") || "إضافي"}</th>
                <th className="text-center px-3 py-3 text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t("hr_short") || "ناقص"}</th>
                <th className="text-end px-3 py-3 text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t("hr_base_salary") || "الراتب الأساسي"}</th>
                <th className="text-end px-3 py-3 text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t("hr_deduction") || "خصم"}</th>
                <th className="text-end px-4 py-3 text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t("hr_net") || "الصافي"}</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-muted-foreground">{t("loading") || "جارٍ التحميل..."}</td></tr>
              )}
              {!loading && filteredEmployees.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-muted-foreground">{t("no_data") || "لا توجد بيانات"}</td></tr>
              )}
              {!loading && filteredEmployees.map(row => (
                <EmployeeRow
                  key={row.employee_id}
                  row={row}
                  expanded={expanded === row.employee_id}
                  onToggle={() => setExpanded(expanded === row.employee_id ? null : row.employee_id)}
                  t={t}
                  lang={lang}
                />
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Cards (mobile) */}
      <div className="md:hidden space-y-3">
        {loading && <Card className="p-8 text-center text-muted-foreground">{t("loading") || "جارٍ التحميل..."}</Card>}
        {!loading && filteredEmployees.length === 0 && <Card className="p-8 text-center text-muted-foreground border-dashed">{t("no_data") || "لا توجد بيانات"}</Card>}
        {!loading && filteredEmployees.map(row => <EmployeeCardMobile key={row.employee_id} row={row} t={t} lang={lang} />)}
      </div>
    </div>
  );
}
